import store from '@/store'
import ExcellentExport from 'excellentexport'
import { CURRENCY_INDEXED } from '@/consts/app'
import { createTmpFile } from '@/utils/io/fs'
import { toast } from '@/utils/ui'
import { getDownload } from '@/utils/api/utils'

const { cordova } = window

const isPlural = (subject) => subject === true || ((subject && subject.length) || subject) > 1

const _isRealHoliday = ({ type }) => type !== 'observance'
export const getRealHoliday = (isHoliday) => {
  if (!isHoliday) {
    return false
  }
  if (Array.isArray(isHoliday)) {
    return isHoliday.find(_isRealHoliday) || false
  }
  return _isRealHoliday(isHoliday)
    ? isHoliday
    : false
}

/**
 * Fonction basique de pluralisation d'un mot
 * @param w
 * @param subject
 * @param char
 */
export const plz = (w, subject = [], char = 's') => isPlural(subject)
  ? w + char
  : w

/**
 * Factory de pluralisation
 *
 * @param single
 * @param plural
 * @returns {function(Array)}
 */
export const plzFactory = (single, plural) => (subject) => isPlural(subject)
  ? plural
  : single

/**
 * Afficher des logs uniquement en dev
 * Le premier argument peut définir le log lvl `error`, `log`, 'warning`
 * @param args
 */
export const devLog = (...args) => {
  const [message, ...rest] = args
  if (window.showLogs || process.env.DEV) {
    const logLevels = ['error', 'log', 'warn', 'info']
    const mth = logLevels.indexOf(message) !== -1 ? message : null
    const _args = typeof message === 'string' && mth === null
      ? ['[DEV] ' + message, ...rest]
      : ['[DEV]', ...args]
    console[(mth || 'info')](..._args)
  }
}

/**
 * Extrait les propriétés d'un objet
 * @param {{}} o
 * @param {Array} a
 */
export const extractProperties = (o, a = []) => {
  const _o = {}
  a.forEach((k) => {
    _o[k] = o[k]
  })
  return _o
}

/**
 * Récupère la propriété `slug` si le sujet est un objet, sinon retourne sa valeur
 * @param v
 * @returns {*}
 */
export const getSlug = (v) =>
  typeof v === 'object'
    ? v.slug
    : v

/**
 * Split une chaine et retourne les élement en integer
 * @param {String} str
 * @param {String} separator
 * @returns {number[]}
 */
export const splitInt = (str, separator = '-') => str.split(separator).map((s) => parseInt(s))

/**
 * Lit un fichier et le retourn en base 64
 * @param {File} f
 * @returns {Promise<{mimeType: string, base64: string}>}
 */
export const readFileBase64 = (f) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.readAsDataURL(f)
  reader.onload = () => resolve({
    name: f.name,
    mimeType: f.type,
    base64: reader.result
  })
  reader.onerror = error => reject(error)
})

/**
 * Parse une base64 URL
 * @param url
 * @returns {{data: string, contentType: string}}
 * @private
 */
const _parseBase64URL = (url = '') => {
  const [type, data] = url.split(';base64,')
  return {
    contentType: type.substr(5),
    data
  }
}

/**
 * Récupère un ficher au format base64 depuis une url
 * @param url
 * @private
 * @returns {Promise<{data: string, contentType: string}>}
 */
const _downloadFileUrl = async (url) => {
  const { base64 } = await getDownload(url)
  return _parseBase64URL(base64)
}

/**
 * Génère une blob a partir d'une chaine en base64
 * @param b64Data
 * @param contentType
 * @param sliceSize
 * @returns {Blob}
 */
export const base64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentType })
}

export const truncToFixed = (nb, fractionDigits = 2) => {
  return nb ? +parseFloat(nb).toFixed(fractionDigits) : 0.00
}

export const truncToFixedCurrency = (nb, currency) => {
  const parsedFloat = parseFloat(nb).toLocaleString(
    undefined,
    {
      style: 'currency',
      currency: currency || store.getters['user/company'].currency || 'EUR'
    })
  return parsedFloat.replace('NaN', '0,00')
}

/**
 * Fonction générique d'export d'un tableau
 * @param table
 * @param fileName
 */
export const exportTable = (table, { fileName = 'export' } = {}) => {
  const link = document.createElement('a')
  const emptyNode = document.createElement('div')
  const fixValue = (v) => {
    emptyNode.innerHTML = v.replace(/<\//g, ' </')
    return emptyNode.innerText.trim()
  }
  link.addEventListener('click', function () {
    return ExcellentExport.convert({ anchor: this, filename: fileName, format: 'xlsx' }, [{
      name: 'sheet',
      from: {
        table: table
      },
      fixValue
    }])
  })
  link.setAttribute('download', fileName + '.xlsx')
  document.body.appendChild(link)

  link.click()
}

const httpFileCache = {}

/**
 * Télécharger un fichier
 * @param fileName
 * @param url
 */
export const downloadFile = async (fileName, url) => {
  if (cordova) {
    let fileUrl
    let contentType
    const isHttp = url.indexOf('http') === 0
    if (isHttp && httpFileCache[url]) {
      fileUrl = httpFileCache[url].fileUrl
      contentType = httpFileCache[url].contentType
    } else {
      const oBase64 = isHttp
        ? await _downloadFileUrl(url)
        : _parseBase64URL(url)
      contentType = oBase64.contentType
      const file = await createTmpFile(fileName, await base64toBlob(oBase64.data, contentType))
      fileUrl = file.toURL()
      if (isHttp) {
        httpFileCache[url] = { fileUrl, contentType }
      }
    }
    cordova.plugins.fileOpener2.showOpenWithDialog(
      fileUrl,
      contentType,
      {
        error: () => toast.negative(`Impossible d'ouvrir le fichier`),
        success: () => devLog(`Ouverture du fichier ${fileUrl}`)
      }
    )
  } else {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.target = '_blank'
    link.click()
  }
}

/**
 * Récupère les information de la devise
 * @param v
 * @returns {*}
 */
export const getCurrency = (v) => (CURRENCY_INDEXED[v] || CURRENCY_INDEXED.EUR)

const regExpHtml = /<\/?[a-z][\s\S]*>/i

/**
 * Converti un ensemble clé d'objet en html pour les wysiwyg
 * @param params
 * @param aKeys
 */
export const ln2brConverter = (params = {}, aKeys = []) => {
  const o = {
    ...params
  }
  aKeys.forEach((k) => {
    if (_.isObject(k)) {
      o[k.key] = ln2brConverter(params[k.key], k.keys)
      return
    }
    const s = o[k]
    if (!s || regExpHtml.test(s)) {
      return
    }
    o[k] = s.split('\n').map((line) => `<div>${line || '<br>'}</div>`).join('')
  })
  return o
}

/**
 * Charge un script de manière asynchrone
 * @param url
 * @returns {Promise<unknown>}
 */
export const getScript = (url) => new Promise((resolve, reject) => {
  const s = document.createElement('script')
  s.src = url
  s.onload = resolve
  s.onerror = reject
  document.head.appendChild(s)
})
